import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  MenuItem,
  Alert
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';

dayjs.extend(isoWeek);

const PRESETS = [
  { label: 'This Week', value: 'this_week' },
  { label: 'Last Week', value: 'last_week' },
  { label: 'This Month', value: 'this_month' },
  { label: 'Last Month', value: 'last_month' },
  { label: 'This Quarter', value: 'this_quarter' },
  { label: 'Last Quarter', value: 'last_quarter' },
  { label: 'Custom Range', value: 'custom' },
];

export default function AnalyticsDateFilter({ onApply }) {
  const [preset, setPreset] = useState('this_quarter');
  const [startDate, setStartDate] = useState(dayjs().startOf('quarter'));
  const [endDate, setEndDate] = useState(dayjs().endOf('quarter'));
  const [error, setError] = useState('');

  useEffect(() => {
    if (preset === 'custom') return; // do nothing

    const now = dayjs();
    let start = startDate;
    let end = endDate;

    switch (preset) {
      case 'this_week':
        start = now.startOf('week');
        end = now.endOf('week');
        break;
      case 'last_week':
        start = now.subtract(1, 'week').startOf('week');
        end = now.subtract(1, 'week').endOf('week');
        break;
      case 'this_month':
        start = now.startOf('month');
        end = now.endOf('month');
        break;
      case 'last_month':
        start = now.subtract(1, 'month').startOf('month');
        end = now.subtract(1, 'month').endOf('month');
        break;
      case 'this_quarter':
        start = now.startOf('quarter');
        end = now.endOf('quarter');
        break;
      case 'last_quarter':
        start = now.subtract(1, 'quarter').startOf('quarter');
        end = now.subtract(1, 'quarter').endOf('quarter');
        break;
      default:
        return;
    }

    // only update if different
    if (!start.isSame(startDate) || !end.isSame(endDate)) {
      setStartDate(start);
      setEndDate(end);
      onApply({ start, end });
    }
  }, [preset, startDate, endDate, onApply]);  

  const handleApply = () => {
    if (endDate.isBefore(startDate)) {
      setError('End date must be after start date');
    } else {
      setError('');
      onApply({ start: startDate, end: endDate });
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
      <TextField
        select
        label="Preset"
        value={preset}
        onChange={(e) => setPreset(e.target.value)}
        sx={{ width: 180 }}
      >
        {PRESETS.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </TextField>

      <DatePicker
        label="Start Date"
        value={startDate}
        views={['year', 'month', 'day']}
        onChange={(newValue) => {
          setStartDate(dayjs(newValue));
          setPreset('custom');
        }}
        renderInput={(params) => <TextField {...params} sx={{ width: 200 }} />}
      />
      <DatePicker
        label="End Date"
        value={endDate}
        views={['year', 'month', 'day']}
        onChange={(newValue) => {
          setEndDate(dayjs(newValue));
          setPreset('custom');
        }}
        renderInput={(params) => <TextField {...params} sx={{ width: 200 }} />}
      />

      <Button variant="contained" onClick={handleApply}>
        Apply
      </Button>

      {error && (
        <Alert severity="error" sx={{ ml: 1 }}>
          {error}
        </Alert>
      )}
    </Box>
  );
}
